import {
  AlertCircle,
  Building2,
  Check,
  CheckCircle2,
  ChevronDown,
  Edit3,
  Eye,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Store,
  Trash2,
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Link,
  useNavigate,
} from "react-router-dom";

import establishmentsApi from "../../api/establishmentsApi";
import { useConfirm } from "../../components/feedback/ConfirmProvider";
import { useLanguage } from "../../contexts/LanguageContext";
import translateApiError from "../../utils/translateApiError";
import "../../styles/Establishments/establishments.css";

const translateFallback = (
  t,
  key,
  fallback,
) => {
  const value = t(key);

  return value === key ? fallback : value;
};

const getRecords = (response) => {
  if (Array.isArray(response?.data?.data)) {
    return response.data.data;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  return [];
};

const getMeta = (response) => {
  return response?.data?.meta || null;
};

const getOwnerName = (record) => {
  return (
    record?.owner?.name ||
    record?.manager?.name ||
    record?.user?.name ||
    record?.owner_name ||
    record?.manager_name ||
    "-"
  );
};

const getBusinessName = (record) => {
  return (
    record?.business?.name ||
    record?.business_name ||
    "-"
  );
};

const formatDate = (
  value,
  language,
) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  try {
    return new Intl.DateTimeFormat(
      language || "fr",
      {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      },
    ).format(date);
  } catch {
    const day = String(
      date.getDate(),
    ).padStart(2, "0");

    const month = String(
      date.getMonth() + 1,
    ).padStart(2, "0");

    return `${day}/${month}/${date.getFullYear()}`;
  }
};

function EstablishmentsFilterSelect({
  value,
  options,
  onChange,
  label,
}) {
  const selectRef = useRef(null);

  const [open, setOpen] =
    useState(false);

  const currentOption =
    options.find(
      (option) =>
        option.value === value,
    ) || options[0];

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleOutsideClick = (
      event,
    ) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(
          event.target,
        )
      ) {
        setOpen(false);
      }
    };

    const handleEscape = (
      event,
    ) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick,
    );

    document.addEventListener(
      "keydown",
      handleEscape,
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick,
      );

      document.removeEventListener(
        "keydown",
        handleEscape,
      );
    };
  }, [open]);

  return (
    <div
      ref={selectRef}
      className={`establishments-filter-select ${
        open
          ? "establishments-filter-select--open"
          : ""
      }`}
    >
      <button
        type="button"
        className="establishments-filter-select__trigger"
        onClick={() =>
          setOpen(
            (state) => !state,
          )
        }
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>
          {currentOption?.label ||
            label}
        </span>

        <ChevronDown size={16} />
      </button>

      {open && (
        <div
          className="establishments-filter-select__menu"
          role="listbox"
        >
          {options.map((option) => {
            const selected =
              option.value === value;

            return (
              <button
                key={option.value}
                type="button"
                className={`establishments-filter-select__option ${
                  selected
                    ? "establishments-filter-select__option--active"
                    : ""
                }`}
                onClick={() => {
                  setOpen(false);
                  onChange(
                    option.value,
                  );
                }}
                role="option"
                aria-selected={
                  selected
                }
              >
                <span>
                  {option.label}
                </span>

                {selected && (
                  <Check size={14} />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EstablishmentStatusSelect({
  record,
  t,
  onChange,
}) {
  const selectRef = useRef(null);

  const [open, setOpen] =
    useState(false);

  const options = [
    {
      value: "active",
      label: translateFallback(
        t,
        "common.active",
        "Active",
      ),
    },
    {
      value: "inactive",
      label: translateFallback(
        t,
        "menus.status.inactive",
        "Inactive",
      ),
    },
  ];

  const currentValue =
    record.is_active
      ? "active"
      : "inactive";

  const currentOption =
    options.find(
      (option) =>
        option.value ===
        currentValue,
    ) || options[0];

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleOutsideClick = (
      event,
    ) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(
          event.target,
        )
      ) {
        setOpen(false);
      }
    };

    const handleEscape = (
      event,
    ) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick,
    );

    document.addEventListener(
      "keydown",
      handleEscape,
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick,
      );

      document.removeEventListener(
        "keydown",
        handleEscape,
      );
    };
  }, [open]);

  return (
    <div
      ref={selectRef}
      className={`establishments-status-select establishments-status-select--${currentValue} ${
        open
          ? "establishments-status-select--open"
          : ""
      }`}
    >
      <button
        type="button"
        className="establishments-status-select__trigger"
        onClick={() =>
          setOpen(
            (value) => !value,
          )
        }
        aria-label={translateFallback(
          t,
          "establishments.table.status",
          "Status",
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>
          {currentOption.label}
        </span>

        <ChevronDown size={14} />
      </button>

      {open && (
        <div
          className="establishments-status-select__menu"
          role="listbox"
        >
          {options.map((option) => {
            const selected =
              option.value ===
              currentValue;

            return (
              <button
                key={option.value}
                type="button"
                className={`establishments-status-select__option ${
                  selected
                    ? "establishments-status-select__option--active"
                    : ""
                }`}
                onClick={() => {
                  setOpen(false);

                  onChange(
                    record,
                    option.value ===
                      "active",
                  );
                }}
                role="option"
                aria-selected={
                  selected
                }
              >
                <span>
                  {option.label}
                </span>

                {selected && (
                  <Check size={14} />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Establishments() {
  const navigate = useNavigate();

  const { t, language } =
    useLanguage();

  const { confirm } =
    useConfirm();

  const [direction, setDirection] =
    useState(
      document.documentElement.dir ||
        "ltr",
    );

  const isRtl =
    direction === "rtl";

  const [records, setRecords] =
    useState([]);

  const [meta, setMeta] =
    useState(null);

  const [search, setSearch] =
    useState("");

  const [status, setStatus] =
    useState("all");

  const [type, setType] =
    useState("all");

  const [page, setPage] =
    useState(1);

  const [loading, setLoading] =
    useState(true);

  const [message, setMessage] =
    useState(null);

  // Keep this page synchronized with the global application direction.
  useEffect(() => {
    const updateDirection = () => {
      setDirection(
        document.documentElement.dir ||
          "ltr",
      );
    };

    updateDirection();

    const observer =
      new MutationObserver(
        updateDirection,
      );

    observer.observe(
      document.documentElement,
      {
        attributes: true,
        attributeFilter: [
          "dir",
          "lang",
        ],
      },
    );

    return () => {
      observer.disconnect();
    };
  }, []);

  const statusFilterOptions =
    useMemo(
      () => [
        {
          value: "all",
          label: translateFallback(
            t,
            "establishments.filters.allStatuses",
            "All statuses",
          ),
        },
        {
          value: "active",
          label: translateFallback(
            t,
            "common.active",
            "Active",
          ),
        },
        {
          value: "inactive",
          label: translateFallback(
            t,
            "menus.status.inactive",
            "Inactive",
          ),
        },
      ],
      [t],
    );

  const typeFilterOptions =
    useMemo(
      () => [
        {
          value: "all",
          label: translateFallback(
            t,
            "establishments.filters.allTypes",
            "All types",
          ),
        },
        ...[
          "restaurant",
          "cafe",
          "snack",
          "bakery",
          "hotel",
          "riad",
          "other",
        ].map((option) => ({
          value: option,
          label: translateFallback(
            t,
            `establishments.types.${option}`,
            option,
          ),
        })),
      ],
      [t],
    );

  const activeCount =
    useMemo(() => {
      return records.filter(
        (record) =>
          Boolean(record.is_active),
      ).length;
    }, [records]);

  const inactiveCount =
    records.length -
    activeCount;

  const showMessage = (
    variant,
    text,
  ) => {
    setMessage({
      variant,
      text,
    });

    window.setTimeout(() => {
      setMessage(null);
    }, variant === "success"
      ? 2600
      : 5200);
  };

  // Load establishments using the selected filters.
  const loadRecords = async (
    nextPage = page,
  ) => {
    try {
      setLoading(true);

      const response =
        await establishmentsApi.getEstablishments(
          {
            search:
              search.trim() ||
              undefined,

            status:
              status === "all"
                ? undefined
                : status,

            type:
              type === "all"
                ? undefined
                : type,

            page: nextPage,
            per_page: 15,
          },
        );

      setRecords(
        getRecords(response),
      );

      setMeta(
        getMeta(response),
      );
    } catch (error) {
      showMessage(
        "error",
        translateApiError(
          error,
          t,
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecords(1);
    setPage(1);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, type]);

  useEffect(() => {
    loadRecords(page);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSearchSubmit = (
    event,
  ) => {
    event.preventDefault();

    setPage(1);
    loadRecords(1);
  };

  // Update one establishment's active state.
  const updateRecordStatus =
    async (
      record,
      nextIsActive,
    ) => {
      if (
        Boolean(
          record.is_active,
        ) === nextIsActive
      ) {
        return;
      }

      try {
        await establishmentsApi.updateStatus(
          record.id,
          nextIsActive,
        );

        await loadRecords(page);

        showMessage(
          "success",
          translateFallback(
            t,
            "establishments.messages.statusUpdated",
            "Establishment status updated successfully.",
          ),
        );
      } catch (error) {
        showMessage(
          "error",
          translateApiError(
            error,
            t,
          ),
        );
      }
    };

  // Delete an establishment after confirmation.
  const deleteRecord =
    async (record) => {
      const approved =
        await confirm({
          title:
            translateFallback(
              t,
              "establishments.confirmDeleteTitle",
              "Delete establishment?",
            ),

          message:
            translateFallback(
              t,
              "establishments.confirmDeleteMessage",
              "This action can affect menus, products, orders and staff linked to this establishment.",
            ),

          itemName:
            record.name,

          confirmText:
            translateFallback(
              t,
              "common.delete",
              "Delete",
            ),

          cancelText:
            translateFallback(
              t,
              "common.cancel",
              "Cancel",
            ),

          closeText:
            translateFallback(
              t,
              "common.close",
              "Close",
            ),

          variant: "danger",
        });

      if (!approved) {
        return;
      }

      try {
        await establishmentsApi.deleteEstablishment(
          record.id,
        );

        await loadRecords(page);

        showMessage(
          "success",
          translateFallback(
            t,
            "establishments.messages.deleted",
            "Establishment deleted successfully.",
          ),
        );
      } catch (error) {
        showMessage(
          "error",
          translateApiError(
            error,
            t,
          ),
        );
      }
    };

  return (
    <main
      className={`establishments-page ${
        isRtl
          ? "establishments-page--rtl"
          : "establishments-page--ltr"
      }`}
      dir={direction}
    >
      <section className="establishments-page__hero">
        <div className="establishments-page__hero-copy">
          <p className="establishments-page__eyebrow">
            <ShieldCheck size={15} />

            {translateFallback(
              t,
              "establishments.eyebrow",
              "Super admin",
            )}
          </p>

          <h1>
            {translateFallback(
              t,
              "establishments.title",
              "Establishments",
            )}
          </h1>

          <p>
            {translateFallback(
              t,
              "establishments.description",
              "Manage restaurant establishments, managers, contact details, ordering settings and public menu access.",
            )}
          </p>
        </div>

        <div className="establishments-page__hero-actions">
          <button
            type="button"
            className="establishments-page__secondary-button"
            onClick={() =>
              loadRecords(page)
            }
          >
            <RefreshCw size={17} />

            {translateFallback(
              t,
              "common.refresh",
              "Refresh",
            )}
          </button>

          <button
            type="button"
            className="establishments-page__primary-button"
            onClick={() =>
              navigate(
                "/establishments/new",
              )
            }
          >
            <Plus size={17} />

            {translateFallback(
              t,
              "establishments.actions.add",
              "Add establishment",
            )}
          </button>
        </div>
      </section>

      {message && (
        <div
          className={`establishments-page__message establishments-page__message--${message.variant}`}
          role="alert"
        >
          {message.variant ===
          "success" ? (
            <CheckCircle2 size={18} />
          ) : (
            <AlertCircle size={18} />
          )}

          <span>
            {message.text}
          </span>
        </div>
      )}

      <section className="establishments-page__stats">
        <article>
          <span>
            {translateFallback(
              t,
              "establishments.stats.total",
              "Total establishments",
            )}
          </span>

          <strong>
            {meta?.total ??
              records.length}
          </strong>
        </article>

        <article>
          <span>
            {translateFallback(
              t,
              "establishments.stats.active",
              "Active",
            )}
          </span>

          <strong>
            {activeCount}
          </strong>
        </article>

        <article>
          <span>
            {translateFallback(
              t,
              "establishments.stats.inactive",
              "Inactive",
            )}
          </span>

          <strong>
            {inactiveCount}
          </strong>
        </article>
      </section>

      <section className="establishments-page__panel">
        <div className="establishments-page__toolbar">
          <form
            className="establishments-page__search"
            onSubmit={
              handleSearchSubmit
            }
          >
            <Search size={17} />

            <input
              type="search"
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value,
                )
              }
              placeholder={translateFallback(
                t,
                "establishments.filters.searchPlaceholder",
                "Search by name, business, manager, phone or email...",
              )}
            />

            <button type="submit">
              {translateFallback(
                t,
                "common.search",
                "Search",
              )}
            </button>
          </form>

          <div className="establishments-page__filters">
            <EstablishmentsFilterSelect
              value={status}
              options={
                statusFilterOptions
              }
              onChange={setStatus}
              label={translateFallback(
                t,
                "establishments.filters.allStatuses",
                "All statuses",
              )}
            />

            <EstablishmentsFilterSelect
              value={type}
              options={
                typeFilterOptions
              }
              onChange={setType}
              label={translateFallback(
                t,
                "establishments.filters.allTypes",
                "All types",
              )}
            />
          </div>
        </div>

        {loading ? (
          <div className="establishments-page__state">
            <Loader2
              size={30}
              className="establishments-page__spin"
            />

            <strong>
              {translateFallback(
                t,
                "establishments.loading",
                "Loading establishments...",
              )}
            </strong>
          </div>
        ) : records.length ? (
          <>
            <div className="establishments-page__table-wrap">
              <table className="establishments-page__table">
                <thead>
                  <tr>
                    <th>
                      {translateFallback(
                        t,
                        "establishments.table.establishment",
                        "Establishment",
                      )}
                    </th>

                    <th>
                      {translateFallback(
                        t,
                        "establishments.table.business",
                        "Business",
                      )}
                    </th>

                    <th>
                      {translateFallback(
                        t,
                        "establishments.table.manager",
                        "Manager",
                      )}
                    </th>

                    <th>
                      {translateFallback(
                        t,
                        "establishments.table.type",
                        "Type",
                      )}
                    </th>

                    <th>
                      {translateFallback(
                        t,
                        "establishments.table.status",
                        "Status",
                      )}
                    </th>

                    <th>
                      {translateFallback(
                        t,
                        "establishments.table.created",
                        "Created",
                      )}
                    </th>

                    <th>
                      {translateFallback(
                        t,
                        "establishments.table.actions",
                        "Actions",
                      )}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {records.map(
                    (record) => (
                      <tr
                        key={
                          record.id
                        }
                      >
                        <td>
                          <div className="establishments-page__entity">
                            <span>
                              <Store size={18} />
                            </span>

                            <div>
                              <strong>
                                {record.name ||
                                  "-"}
                              </strong>

                              <small>
                                {record.email ||
                                  record.phone ||
                                  record.slug ||
                                  "-"}
                              </small>
                            </div>
                          </div>
                        </td>

                        <td>
                          {getBusinessName(
                            record,
                          )}
                        </td>

                        <td>
                          {getOwnerName(
                            record,
                          )}
                        </td>

                        <td>
                          <span className="establishments-page__pill">
                            {translateFallback(
                              t,
                              `establishments.types.${record.type}`,
                              record.type ||
                                "-",
                            )}
                          </span>
                        </td>

                        <td>
                          <EstablishmentStatusSelect
                            record={
                              record
                            }
                            t={t}
                            onChange={
                              updateRecordStatus
                            }
                          />
                        </td>

                        <td>
                          {formatDate(
                            record.created_at,
                            language,
                          )}
                        </td>

                        <td>
                          <div className="establishments-page__row-actions">
                            <Link
                              to={`/establishments/${record.id}`}
                              title={translateFallback(
                                t,
                                "establishments.actions.view",
                                "View details",
                              )}
                              aria-label={translateFallback(
                                t,
                                "establishments.actions.view",
                                "View details",
                              )}
                            >
                              <Eye size={16} />
                            </Link>

                            <Link
                              to={`/establishments/${record.id}/edit`}
                              title={translateFallback(
                                t,
                                "establishments.actions.edit",
                                "Edit",
                              )}
                              aria-label={translateFallback(
                                t,
                                "establishments.actions.edit",
                                "Edit",
                              )}
                            >
                              <Edit3 size={16} />
                            </Link>

                            <button
                              type="button"
                              className="is-danger"
                              onClick={() =>
                                deleteRecord(
                                  record,
                                )
                              }
                              title={translateFallback(
                                t,
                                "common.delete",
                                "Delete",
                              )}
                              aria-label={translateFallback(
                                t,
                                "common.delete",
                                "Delete",
                              )}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>

            {meta?.last_page >
            1 ? (
              <div className="establishments-page__pagination">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() =>
                    setPage(
                      (current) =>
                        current -
                        1,
                    )
                  }
                >
                  {translateFallback(
                    t,
                    "orders.pagination.previous",
                    "Previous",
                  )}
                </button>

                <span>
                  {page} /{" "}
                  {
                    meta.last_page
                  }
                </span>

                <button
                  type="button"
                  disabled={
                    page >=
                    meta.last_page
                  }
                  onClick={() =>
                    setPage(
                      (current) =>
                        current +
                        1,
                    )
                  }
                >
                  {translateFallback(
                    t,
                    "orders.pagination.next",
                    "Next",
                  )}
                </button>
              </div>
            ) : null}
          </>
        ) : (
          <div className="establishments-page__state">
            <Building2 size={34} />

            <strong>
              {translateFallback(
                t,
                "establishments.empty.title",
                "No establishments found",
              )}
            </strong>

            <span>
              {translateFallback(
                t,
                "establishments.empty.description",
                "Create the first establishment to connect managers, menus and QR codes.",
              )}
            </span>
          </div>
        )}
      </section>
    </main>
  );
}

export default Establishments;